import { Stack, TextField, styled, InputAdornment, Box } from '@mui/material';

import { useDispatch } from 'react-redux';

import { ReactComponent as FilterIcon } from 'common/assets/images/Filter.svg';
import { ReactComponent as GridIcon } from 'common/assets/images/grid.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as SortIcon } from 'common/assets/images/sort-icon.svg';
import { ReactComponent as TableIcon } from 'common/assets/images/table.svg';
import { ViewType } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { setSimulationViewTypeValue } from 'common/store/reducer/simulationReducer';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { ISectionToolbar } from 'features/AiSimulation/api/type-def';

import styles from './SectionToolbar.module.scss';

const { searchFieldBackground, searchFieldBorderRadius } = styles;

export enum ToolbarType {
	SEARCH,
	SORT,
	FILTER,
	TOGGLE_VIEW,
}

const StyledTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});

const {
	IS_AI_LANDING_PAGE_SECTION_SEARCH_ENABLED,
	IS_AI_LANDING_PAGE_SECTION_SORT_ENABLED,
	IS_AI_LANDING_PAGE_SECTION_FILTER_ENABLED,
} = FeatureFlagsKeysEnum;

const SectionToolbar = (props: ISectionToolbar) => {
	const dispatch = useDispatch();

	const { types, viewType, onToggleView } = props;

	const isAILandingPageSectionSearchEnabled = useFeatureFlag(
		IS_AI_LANDING_PAGE_SECTION_SEARCH_ENABLED,
	);
	const isAILandingPageSectionSortEnabled = useFeatureFlag(
		IS_AI_LANDING_PAGE_SECTION_SORT_ENABLED,
	);
	const isAILandingPageSectionFilterEnabled = useFeatureFlag(
		IS_AI_LANDING_PAGE_SECTION_FILTER_ENABLED,
	);

	const handleToggleViewType = (type: ViewType) => {
		onToggleView?.(type);
		dispatch(setSimulationViewTypeValue(type));
	};

	const getIconClass = (targetType: ViewType) =>
		[
			styles.icon,
			viewType === targetType ? styles.active : styles.inactive,
		].join(' ');

	return (
		<Stack
			direction="row"
			gap={1}
			alignItems="center"
			alignSelf="flex-end"
			justifyContent="flex-end"
			data-testid="toolbar-stack"
		>
			{isAILandingPageSectionSearchEnabled &&
				types.includes(ToolbarType.SEARCH) && (
					<StyledTextField
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlined className={styles['input-search-icon']} />
								</InputAdornment>
							),
						}}
						className={styles['search-input']}
						fullWidth={false}
						placeholder="Search"
						size="small"
					/>
				)}

			{isAILandingPageSectionSortEnabled &&
				types.includes(ToolbarType.SORT) && (
					<div className={styles.btn}>
						<SortIcon />
					</div>
				)}

			{isAILandingPageSectionFilterEnabled &&
				types.includes(ToolbarType.FILTER) && (
					<div className={styles.btn}>
						<FilterIcon />
					</div>
				)}

			{types.includes(ToolbarType.TOGGLE_VIEW) && (
				<div className={styles['toggle-view']}>
					<Box
						data-testId="grid-btn"
						onClick={() => handleToggleViewType(ViewType.GRID)}
						className={getIconClass(ViewType.GRID)}
					>
						<GridIcon />
					</Box>
					<Box
						data-testId="table-btn"
						onClick={() => handleToggleViewType(ViewType.TABLE)}
						className={getIconClass(ViewType.TABLE)}
					>
						<TableIcon />
					</Box>
				</div>
			)}
		</Stack>
	);
};

export default SectionToolbar;
